<template>
	<div>
		<span class="caption text-uppercase" v-if="!disableOrder && !hideHelp"
			>Drag items to rearrange. Click to edit.</span
		>

		<v-container
			fluid
			grid-list-xl
			v-if="collection"
			@dragover="dragover"
			@dragleave="dragleave"
			@drop="drop"
		>
			<draggable
				:list="collection"
				@change="$emit('change')"
				class="v-row wrap d-flex flex-wrap"
				style="width: 100%"
			>
				<v-flex
					:class="[breakpoints, itemMargin]"
					:key="index"
					@click="itemClick(item)"
					cols="auto"
					:style="`max-width: ${maxWidth}`"
					v-for="(item, index) in collection"
				>
					<slot v-bind:index="index" v-bind:item="item" />
				</v-flex>
				<slot name="new-item">
					<NewCard
						v-if="!disableNew"
						:class="breakpoints"
						@click.native="view()"
					/>
				</slot>
			</draggable>
		</v-container>
	</div>
</template>

<script>
import draggable from 'vuedraggable'
import Dialog from '@/components/dialogs/DialogEdit'
import NewCard from './NewCard'

import Messages from '@/helpers/message-helper'
import FileService from '@/services/FileService'

export default {
	name: 'ItemCollection',
	props: {
		collection: Array,
		form: Object,
		title: String,
		breakpoints: String,
		disableClick: Boolean,
		dialogWidth: {
			type: [Number, String],
			default: 500,
		},
		maxWidth: {
			type: String,
			default: '400px',
		},
		disableOrder: Boolean,
		hideHelp: Boolean,
		disableNew: Boolean,
		isFileUpload: Boolean,
		pushToTop: Boolean,
		itemMargin: {
			type: String,
			default: 'ma-2',
		},
		uploadFn: [Function, Object],
	},
	components: { draggable, NewCard },
	methods: {
		emitChange() {
			this.$emit('change')
		},
		itemClick(item) {
			if (!this.disableClick) {
				this.view(item)
			}
		},
		view(item) {
			let index = this.collection.indexOf(item)
			let title = `${index < 0 ? 'Add' : 'Edit'} ${this.title}`
			this.showDialog(title, this.form, index, item)
		},
		save(item, index) {
			index == -1
				? this.pushToTop
					? this.collection.unshift(item)
					: this.collection.push(item)
				: this.$set(this.collection, index, item)
		},
		async remove(index) {
			if (this.isFileUpload) {
				const key = this.collection[index].name
				await this.deleteFile(key)
			}

			this.collection.splice(index, 1)
		},
		async uploadFile(uploadFile) {
			const { name, file } = uploadFile
			return await FileService.uploadS3(name, file)
		},
		async deleteFile(key) {
			return await FileService.deleteS3(key)
		},
		async showDialog(title, component, dataIndex, data) {
			if (!data) data = {} // If new item, data is undefined. Set it to blank object
			if (!data.files) this.$set(data, 'files', []) // Add files object if it does not exist

			const isNew = dataIndex == -1

			var result = await this.$dialog.showAndWait(Dialog, {
				title,
				component,
				data,
				icon: false,
				width: this.dialogWidth || 600,
				hideDelete: isNew,
			})

			if (!result) return
			Messages.showSaving()

			try {
				switch (result.action) {
					case 'save':
						const isNewFile = this.isFileUpload && isNew
						// If custom file upload function is specified, use it or use fallback
						const uploadFn = this.uploadFn ? this.uploadFn : this.uploadFile

						const resultData = isNewFile
							? await uploadFn(result.dialogData)
							: result.dialogData

						this.save(resultData, dataIndex)
						Messages.showSuccess('uploaded')
						break
					case 'remove':
						await this.remove(dataIndex)
						Messages.showSuccess('deleted')
						break
				}
			} catch (error) {
				Messages.showError(error)
			}

			this.emitChange()
		},
		dragover(event) {
			event.preventDefault()
			event.currentTarget.classList.add('file-hover')
		},
		dragleave(event) {
			event.currentTarget.classList.remove('file-hover')
		},
		drop(event) {
			// Drag drop file upload handling
			event.preventDefault()
			Messages.showSaving()

			const { files } = event.dataTransfer
			const uploadFn = this.uploadFn ? this.uploadFn : this.uploadFile

			try {
				files.forEach(async (file) => {
					// uploadFn usually expects a file object named file
					let result = await uploadFn({ file })
					this.save(result, -1)
				})

				Messages.showSuccess('uploaded')
			} catch (error) {
				Messages.showError(error)
			}

			event.currentTarget.classList.remove('file-hover')
		},
	},
}
</script>

<style lang="stylus" scoped>
.file-hover {
	outline: 2px dashed #FF9500;
}
</style>